import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Chip, DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Inline } from "components/layout/reusableLayouts";
import UserStatus from "../../../enums/UserStatus";
import { PersonAddOutlined, PersonRemoveOutlined } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import PasswordIcon from "@mui/icons-material/Password";
import BlockIcon from "@mui/icons-material/Block";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { GET_USERS } from "../../../graphql/user";
import { DEFAULT_PAGINATION_SIZE } from "../../common/Constants";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import {
  employeeFromUserNode,
  type Employee,
} from "../../common/EmployeeSearchAutoComplete";
import AddCardIcon from "@mui/icons-material/AddCard";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import {
  RemoveUserModal,
  ResendUserInviteModal,
  ResetUserPasswordModal,
  SendUserBankAccountInviteModal,
} from "../../common/users/UserModules";
import RenderUserStatus from "../../common/RenderUserStatus";
import Role from "enums/Role";
import { hasRoleAtLeast } from "utils/authorization";
import { filterValues, type UserFilterData } from "utils/users/userFilters";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useCanManageUsers } from "hooks/useCanManageUsers";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { surnameFirstName } from "utils/fullName";
import UserAvatar from "components/common/users/UserAvatar";

interface UserConnection {
  edges: { cursor: string; node: UserNode }[];
}

interface Data extends Employee {
  userId: string;
  name: string;
  userStatus: string;
  role: string;
  cardCount: string;
  hasPersonalBankAccount?: boolean;
  accountName?: string;
  profilePicUrl?: string | null;
}

/** One node of the `users` connection, in the fields this table reads. */
interface UserNode {
  userId: string;
  email: string;
  firstName: string;
  lastName?: string | null;
  profilePicUrl?: string | null;
  status: string;
  role: string;
  phone?: string | null;
  birthdate?: Date | null;
  cards?: { id: string }[] | null;
  hasPhysicalCard?: boolean;
  hasPersonalBankAccount?: boolean;
  address?: Employee["address"];
  company: { businessLegalName: string };
}

interface UsersTableProps {
  handleOpenUserProfileDrawer: (
    userId: string,
    edit?: boolean,
    reactivateUser?: boolean,
  ) => void;
  filterData: UserFilterData;
  refreshUsers?: number;
  handleRefreshUsers: () => void;
}

const UsersTable: React.FC<UsersTableProps> = (props) => {
  const {
    filterData,
    handleOpenUserProfileDrawer,
    refreshUsers,
    handleRefreshUsers,
  } = props;
  const { t } = useTranslation();

  const isManagerOrHigher = useCheckUserRole(Role.Manager);
  const canManage = useCanManageUsers();

  const [openResendUserInviteModal, setOpenResendUserInviteModal] =
    useState<boolean>(false);
  const [openResetUserPasswordModal, setOpenResetUserPasswordModal] =
    useState<boolean>(false);
  const [openRemoveUserModal, setOpenRemoveUserModal] =
    useState<boolean>(false);
  const cardDrawer = useDrawerRecord<Data>();
  const [openSendBankAccountInviteModal, setOpenSendBankAccountInviteModal] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<Data>();

  const defaultPaginationVariables = useMemo(
    () => ({
      first: DEFAULT_PAGINATION_SIZE,
      after: undefined,
      status: filterValues(filterData.status),
      role: filterValues(filterData.role),
      searchTerm: filterData.searchTerm,
      companyId:
        filterData.companyId === "showAll" ? undefined : filterData.companyId,
    }),
    [filterData],
  );

  const extractUsers = (data: Record<string, UserConnection>) => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map((item) => ({
      ...employeeFromUserNode({ ...item.node, id: item.cursor }),
      userId: item.node.userId,
      name: surnameFirstName(item.node),
      profilePicUrl: item.node.profilePicUrl,
      userStatus: item.node.status,
      role: item.node.role,
      cardCount: String(item.node.cards?.length ?? 0),
      hasPersonalBankAccount: item.node.hasPersonalBankAccount,
      accountName: item.node.company.businessLegalName,
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_USERS,
      initialVariables: defaultPaginationVariables,
      extractData: extractUsers,
      refetchIndex: refreshUsers,
    });

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        cell: (user) => ({
          avatar: <UserAvatar size="sm" user={user} />,
          content: (
            <Inline gap="var(--space-2)" align="center">
              <Typography variant="body2">{user.name}</Typography>
              <Chip
                size="sm"
                tone={
                  hasRoleAtLeast(user.role as Role, Role.Admin)
                    ? "brand"
                    : "neutral"
                }
              >
                {t(user.role)}
              </Chip>
            </Inline>
          ),
        }),
      },
      {
        id: "email",
        header: t("Email"),
        cell: (user) => user.email,
      },
      {
        id: "userStatus",
        header: t("User Status"),
        cell: (user) => <RenderUserStatus userStatus={user.userStatus} />,
      },
      {
        id: "cardCount",
        // "Cards", not "Card Count": the table auto-sizes each column to its
        // widest cell, and the header was the widest cell, holding a 100px
        // column open for a one-digit number. A DS `width` hint is not the fix
        // here, it acts as a minimum and overflows the table at any budget.
        header: t("Cards"),
        align: "right",
        cell: (user) => user.cardCount,
      },
    ],
    [t],
  );

  const rowActions = (user: Data): RowAction[] => {
    const isCardOnlyUser = user.role === Role.Cardholder;

    const actions: RowAction[] = [
      ...(isManagerOrHigher
        ? [
            {
              label: t("View User Info"),
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => handleOpenUserProfileDrawer(user.id),
            },
          ]
        : []),
      ...(canManage && user.userStatus !== UserStatus.INACTIVE
        ? [
            {
              label: t("Deactivate User"),
              icon: <PersonRemoveOutlined fontSize="small" />,
              onClick: () => {
                setSelectedUser(user);
                setOpenRemoveUserModal(true);
              },
            },
          ]
        : []),
      ...(canManage && user.userStatus === UserStatus.PENDING && !isCardOnlyUser
        ? [
            {
              label: t("Resend User Invite"),
              icon: <SendIcon fontSize="small" />,
              onClick: () => {
                setSelectedUser(user);
                setOpenResendUserInviteModal(true);
              },
            },
          ]
        : []),
      ...(canManage && !isCardOnlyUser
        ? [
            {
              label: t("Reset User Password"),
              icon: <PasswordIcon fontSize="small" />,
              onClick: () => {
                setSelectedUser(user);
                setOpenResetUserPasswordModal(true);
              },
            },
          ]
        : []),
      ...(canManage &&
      (user.userStatus === UserStatus.ACTIVE ||
        user.userStatus === UserStatus.PENDING)
        ? [
            {
              label: t("Create Card"),
              icon: <AddCardIcon fontSize="small" />,
              onClick: () => {
                cardDrawer.show(user);
              },
            },
          ]
        : []),
      ...(canManage && user.userStatus === UserStatus.INACTIVE
        ? [
            {
              label: t("Reactivate User"),
              icon: <PersonAddOutlined fontSize="small" />,
              onClick: () => handleOpenUserProfileDrawer(user.id, true, true),
            },
          ]
        : []),
      ...(canManage &&
      user.userStatus === UserStatus.ACTIVE &&
      user.hasPersonalBankAccount === false &&
      !isCardOnlyUser
        ? [
            {
              label: t("Send Personal Bank Account Invite"),
              icon: <AccountBalanceOutlinedIcon fontSize="small" />,
              onClick: () => {
                setSelectedUser(user);
                setOpenSendBankAccountInviteModal(true);
              },
            },
          ]
        : []),
    ];

    if (actions.length === 0) {
      return [
        {
          label: t("No Actions Possible"),
          icon: <BlockIcon fontSize="small" />,
          onClick: () => undefined,
        },
      ];
    }

    return actions;
  };

  return (
    <>
      <Box sx={{ height: "100%" }}>
        <DataTable<Data>
          columns={columns}
          rows={dataArray}
          getRowKey={(user) => user.id}
          loading={displayLoading}
          isFetchingMore={isInfiniteScrollFetching}
          onRowClick={(user) => handleOpenUserProfileDrawer(user.id)}
          onScroll={handleScroll}
          emptyState={t("No users found")}
          rowActions={rowActions}
          rowActionsLabel={t("RowActions")}
          loadingMoreText={t("Loading more users")}
          stickyHeader
        />
      </Box>

      <RemoveUserModal
        openRemoveUserModal={openRemoveUserModal}
        setOpenRemoveUserModal={setOpenRemoveUserModal}
        userId={selectedUser?.id ?? ""}
        userEmail={selectedUser?.email ?? ""}
        handleUpdateUser={handleRefreshUsers}
      />

      <ResetUserPasswordModal
        openResetUserPasswordModal={openResetUserPasswordModal}
        setOpenResetUserPasswordModal={setOpenResetUserPasswordModal}
        userEmail={selectedUser?.email ?? ""}
      />

      <ResendUserInviteModal
        openResendUserInviteModal={openResendUserInviteModal}
        setOpenResendUserInviteModal={setOpenResendUserInviteModal}
        userEmail={selectedUser?.email ?? ""}
        handleUpdateUser={handleRefreshUsers}
      />

      <SendUserBankAccountInviteModal
        openSendBankAccountInviteModal={openSendBankAccountInviteModal}
        setOpenSendBankAccountInviteModal={setOpenSendBankAccountInviteModal}
        userId={selectedUser?.id ?? ""}
        userEmail={selectedUser?.email ?? ""}
      />

      {cardDrawer.record && (
        <CardFormDrawer
          open={cardDrawer.open}
          onClose={() => {
            handleRefreshUsers();
            cardDrawer.close();
          }}
          onAfterClose={cardDrawer.afterClose}
          cardholder={employeeFromUserNode(cardDrawer.record)}
        />
      )}
    </>
  );
};

export default UsersTable;
